import { Box, Grid, CircularProgress, Typography, Button, Link, Breadcrumbs, Paper } from "@mui/material";
import { subDays, subHours, format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import OverviewBudget from "./overview/overview-budget";
// import OverviewLatestOrders from "./overview/overview-latest-orders";
// import OverviewLatestProducts from "./overview/overview-latest-products";
import { OverviewSales } from "./overview/overview-sales";
import { OverviewTasksProgress } from "./overview/overview-tasks-progress";
import { OverviewTotalSales } from "./overview/overview-total-customers";
import { OverviewTotalProfit } from "./overview/overview-total-profit";
import { OverviewTraffic } from "./overview/overview-traffic";
import { getReceiptReports, ReceiptReportsResponse } from "services/receipt";
import { getCreditReports, CreditReportResponse } from "services/credits_v2";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ArrowRightIcon } from "@mui/x-date-pickers";

// Comment out unused components
// const OverviewLatestSales = OverviewLatestOrders;

const now = new Date();

const DashboardComponent: React.FC = () => {
  const navigate = useNavigate();
  // State for API data
  const [receiptData, setReceiptData] = useState<ReceiptReportsResponse | null>(null);
  const [creditData, setCreditData] = useState<CreditReportResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Calculate date ranges for reports (last 6 months)
  const endDate = now;
  const startDate = subMonths(now, 6);
  
  // Function to fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch receipt reports
      const receiptReports = await getReceiptReports({
        from: startDate.toISOString(),
        to: endDate.toISOString(),
        groupBy: 'month'
      });
      setReceiptData(receiptReports);
      
      // Fetch credit reports
      const creditReports = await getCreditReports({
        from: startDate.toISOString(),
        to: endDate.toISOString(),
        groupBy: 'month'
      });
      setCreditData(creditReports);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Prepare chart data for receipts
  const prepareReceiptChartData = () => {
    if (!receiptData?.data) return [];
    
    // Last 6 months data
    const monthlyData = Array.isArray(receiptData.data) ? receiptData.data : [];
    
    // Sort by date
    monthlyData.sort((a, b) => new Date(a.month || a.date).getTime() - new Date(b.month || b.date).getTime());
    
    return [
      {
        name: "Receipts",
        data: monthlyData.map(month => month.totalAmount || 0)
      }
    ];
  };
  
  // Prepare chart data for credits
  const prepareCreditChartData = () => {
    if (!creditData?.data) return [];
    
    // Last 6 months data
    const monthlyData = Array.isArray(creditData.data) ? creditData.data : [];
    
    // Sort by date
    monthlyData.sort((a, b) => new Date(a.month || a.date).getTime() - new Date(b.month || b.date).getTime());
    
    return [
      {
        name: "Credits",
        data: monthlyData.map(month => month.creditAmount || 0)
      },
      {
        name: "Debits",
        data: monthlyData.map(month => month.debitAmount || 0)
      }
    ];
  };
  
  // Get month labels for charts
  const getMonthLabels = () => {
    const labels = [];
    let currentDate = startDate;
    
    while (currentDate <= endDate) {
      labels.push(format(currentDate, 'MMM yyyy'));
      currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    }
    
    return labels;
  };
  
  // Calculate total unpaid amounts
  const totalUnpaidReceipts = receiptData?.summary?.totalBalance || 0;
  const totalUnpaidCredits = creditData?.summary?.unpaidCreditAmount ?? 0;
  
  // Calculate net financial position
  const netFinancialPosition = (receiptData?.summary?.totalReceived || 0) - (creditData?.summary?.netAmount || 0);
  
  // Calculate payment success rate
  const receiptPaymentRate = receiptData?.summary ? 
    (receiptData.summary.paidCount / (receiptData.summary.paidCount + receiptData.summary.unpaidCount)) * 100 : 0;
  
  const creditPaymentRate = creditData?.summary ? 
    (creditData.summary.paidCount / creditData.summary.totalTransactions) * 100 : 0;
    
  return (
  <>
    <Box
      component="main"
      sx={{
        flexGrow: 1,
      }}
    >
      <Box mb={3}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link component={RouterLink} to="/" color="inherit">
            Home
          </Link>
          <Link component={RouterLink} to="/reporting" color="inherit">
            Reporting Dashboard
          </Link>
          <Typography color="text.primary">Financial Dashboard</Typography>
        </Breadcrumbs>
      </Box>
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Financial Dashboard
        </Typography>
        <Button
          variant="outlined"
          endIcon={<ArrowRightIcon />}
          component={RouterLink}
          to="/reporting"
        >
          Go to reports
        </Button>
      </Box>
      
      <Box maxWidth="100%">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewBudget
              difference={netFinancialPosition > 0 ? 100 : -100}
              positive={netFinancialPosition > 0}
              sx={{ height: "100%" }}
              value={`TND${Math.abs(netFinancialPosition / 1000).toFixed(1)}k`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewTotalSales
              difference={receiptData?.summary?.totalReceipts || 0}
              positive={true}
              sx={{ height: "100%" }}
              value={`${receiptData?.summary?.totalReceipts || 0}`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewTasksProgress 
              sx={{ height: "100%" }} 
              value={receiptPaymentRate} 
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewTotalProfit 
              sx={{ height: "100%" }} 
              value={`TND${(totalUnpaidReceipts / 1000).toFixed(1)}k`} 
            />
          </Grid>
          <Grid item xs={12} lg={8}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="error">{error}</Typography>
              </Box>
            ) : (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Receipt Trends</Typography>
                  <Button 
                    variant="text" 
                    color="primary" 
                    component={RouterLink}
                    to="/reporting?report=receipts"
                  >
                    View Details
                  </Button>
                </Box>
                <OverviewSales
                  chartSeries={prepareReceiptChartData()}
                  sx={{ height: "100%" }}
                />
              </Box>
            )}            
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="error">{error}</Typography>
              </Box>
            ) : (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Receipt Payment Status</Typography>
                  <Button 
                    variant="text" 
                    color="primary" 
                    component={RouterLink}
                    to="/reporting?report=receipts"
                  >
                    View Details
                  </Button>
                </Box>
                <OverviewTraffic
                  chartSeries={[receiptData?.summary?.paidCount || 0, receiptData?.summary?.unpaidCount || 0]}
                  labels={["Paid Receipts", "Unpaid Receipts"]}
                  sx={{ height: "100%" }}
                />
              </Box>
            )}            
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="error">{error}</Typography>
              </Box>
            ) : (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Credit vs Debit Trends</Typography>
                  <Button 
                    variant="text" 
                    color="primary" 
                    component={RouterLink}
                    to="/reporting?report=credits"
                  >
                    View Details
                  </Button>
                </Box>
                <OverviewSales
                  chartSeries={prepareCreditChartData()}
                  sx={{ height: "100%" }}
                />
              </Box>
            )}
          </Grid>
          {/* Commented out hardcoded product list
          <Grid item xs={12} md={6} lg={4}>
            <OverviewLatestProducts
              products={[
                {
                  id: "5ece2c077e39da27658aa8a9",
                  image: "/assets/products/product-1.png",
                  name: "Fresh Tomatoes",
                  updatedAt: subHours(now, 6).getTime(),
                },
                {
                  id: "5ece2c0d16f70bff2cf86cd8",
                  image: "/assets/products/product-2.png",
                  name: "Organic Carrots",
                  updatedAt: subDays(subHours(now, 8), 2).getTime(),
                },
                {
                  id: "b393ce1b09c1254c3a92c827",
                  image: "/assets/products/product-5.png",
                  name: "Golden Apples",
                  updatedAt: subDays(subHours(now, 1), 1).getTime(),
                },
                {
                  id: "a6ede15670da63f49f752c89",
                  image: "/assets/products/product-6.png",
                  name: "Wheat Grains",
                  updatedAt: subDays(subHours(now, 3), 3).getTime(),
                },
                {
                  id: "bcad5524fe3a2f8f8620ceda",
                  image: "/assets/products/product-7.png",
                  name: "Red Peppers",
                  updatedAt: subDays(subHours(now, 5), 6).getTime(),
                },
              ]}
              sx={{ height: "100%" }}
            />
          </Grid>
          */}

          {/* Commented out hardcoded sales list
          <Grid item xs={12} md={12} lg={8}>
            <OverviewLatestSales
              sales={[
                {
                  id: "f69f88012978187a6c12897f",
                  ref: "AGRI1049",
                  amount: 30.5,
                  customer: {
                    name: "Mohamed Ben Ali", // Tunisian name for localization
                  },
                  createdAt: 1625016400000,
                  status: "pending",
                },
                {
                  id: "9eaa1c7dd4433f413c308ce2",
                  ref: "AGRI1048",
                  amount: 25.1,
                  customer: {
                    name: "Youssef Jebali", // Another localized name
                  },
                  createdAt: 1625016400000,
                  status: "delivered",
                },
                {
                  id: "01a5230c811bd04996ce7c13",
                  ref: "AGRI1047",
                  amount: 10.99,
                  customer: {
                    name: "Saida Toumi",
                  },
                  createdAt: 1624930000000,
                  status: "refunded",
                },
                {
                  id: "1f4e1bd0a87cea23cdb83d18",
                  ref: "AGRI1046",
                  amount: 96.43,
                  customer: {
                    name: "Fares Louati",
                  },
                  createdAt: 1624757200000,
                  status: "pending",
                },
                {
                  id: "9f974f239d29ede969367103",
                  ref: "AGRI1045",
                  amount: 32.54,
                  customer: {
                    name: "Nadia Krimi",
                  },
                  createdAt: 1624670800000,
                  status: "delivered",
                },
                {
                  id: "ffc83c1560ec2f66a1c05596",
                  ref: "AGRI1044",
                  amount: 16.76,
                  customer: {
                    name: "Sami Bouzid",
                  },
                  createdAt: 1624670800000,
                  status: "delivered",
                },
              ]}
              sx={{ height: "100%" }}
            />
          </Grid>
          */}
        </Grid>
      </Box>
    </Box>
  </>
  );
};

export default DashboardComponent;
